import { useMemo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from "lucide-react";
import {
  getInteractionMatrixRules,
  type ClinicalInteractionMatrixRule,
} from "@/domain/clinicalInteractionMatrix";

const EMPTY_RULE: ClinicalInteractionMatrixRule = {
  id: "",
  match_a: "",
  match_b: "",
  category: "farmacodinamica",
  pd_axis: "",
  pk_mechanism: "",
  direction: "aumenta",
  effect: "",
  mechanism: "",
  interpretation: "",
  phenotype: [],
  monitor: [],
  red_flags: [],
  action: "",
  severity: "monitorar",
  confidence: "moderada",
  evidence_grade: "desconhecida",
  source: "",
  source_url: "",
  temporal_relevance: "qualquer",
  acute_window_hours: 6,
  residual_window_hours: 48,
  maintenance_relevant: true,
  dose_dependence: "desconhecida",
  patient_modifiers: [],
  route_modifiers: [],
  formulation_modifiers: [],
  notes: "",
};

const arr = (value: unknown) =>
  Array.isArray(value) ? value.join(", ") : String(value ?? "");

export function InteractionMatrixEditor({
  value,
  onChange,
}: {
  value?: unknown;
  onChange: (value: Record<string, unknown>) => void;
}) {
  const rules = useMemo(() => getInteractionMatrixRules(value), [value]);

  const patchRules = (next: ClinicalInteractionMatrixRule[]) => {
    onChange({
      ...(typeof value === "object" && value && !Array.isArray(value) ? value : {}),
      matrix: next,
      schema_version: "1.0",
    });
  };

  const patchRule = (index: number, patch: Partial<ClinicalInteractionMatrixRule>) => {
    patchRules(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  const patchArray = (index: number, key: keyof ClinicalInteractionMatrixRule, text: string) => {
    patchRule(index, { [key]: text.split(",").map((x) => x.trim()).filter(Boolean) } as any);
  };

  const addRule = () => {
    patchRules([
      ...rules,
      { ...EMPTY_RULE, id: `matrix-${Date.now()}` },
    ]);
  };

  const removeRule = (index: number) => patchRules(rules.filter((_, i) => i !== index));

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 rounded-xl border border-primary/20 bg-primary/5 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm font-medium">Matriz clínica de interação</div>
          <div className="text-[11px] text-muted-foreground">
            Regra estruturada por mecanismo, PK/PD, evidência e relevância temporal. O motor usa a última exposição registrada para atenuar cauda/manutenção.
          </div>
        </div>
        <Button type="button" size="sm" onClick={addRule}>
          <Plus className="mr-1.5 h-4 w-4" />Adicionar regra
        </Button>
      </div>

      {rules.length === 0 ? (
        <div className="rounded-xl border border-dashed p-5 text-sm text-muted-foreground">
          Nenhuma regra específica neste canônico. Adicione uma regra quando houver interação que precise de interpretação além das famílias genéricas do motor.
        </div>
      ) : null}

      {rules.map((rule, index) => (
        <div key={rule.id || index} className="space-y-4 rounded-xl border p-4">
          <div className="flex items-center justify-between gap-2">
            <Badge variant="outline">Regra {index + 1}</Badge>
            <Button type="button" size="icon" variant="ghost" onClick={() => removeRule(index)} aria-label="Remover regra">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Agente A / match">
              <Input value={rule.match_a} onChange={(e) => patchRule(index, { match_a: e.target.value })} placeholder="ex.: bupropiona" />
            </Field>
            <Field label="Agente B / match">
              <Input value={rule.match_b} onChange={(e) => patchRule(index, { match_b: e.target.value })} placeholder="ex.: vortioxetina" />
            </Field>
            <Field label="Categoria">
              <Select value={rule.category} onChange={(v) => patchRule(index, { category: v as any })} options={["farmacodinamica","farmacocinetica","metabolica","contextual","alimentar","comportamental"]} />
            </Field>
            <Field label="Eixo PD">
              <Input value={rule.pd_axis ?? ""} onChange={(e) => patchRule(index, { pd_axis: e.target.value })} placeholder="ex.: serotonina, SNC, noradrenalina" />
            </Field>
            <Field label="Mecanismo PK">
              <Input value={rule.pk_mechanism ?? ""} onChange={(e) => patchRule(index, { pk_mechanism: e.target.value })} placeholder="ex.: inibição CYP2D6" />
            </Field>
            <Field label="Direção">
              <Select value={rule.direction} onChange={(v) => patchRule(index, { direction: v as any })} options={["aumenta","reduz","bidirecional","antagonismo_funcional","incerto"]} />
            </Field>
            <Field label="Efeito clínico / fenótipo">
              <Textarea rows={3} value={rule.effect ?? ""} onChange={(e) => patchRule(index, { effect: e.target.value })} />
            </Field>
            <Field label="Mecanismo integrado">
              <Textarea rows={3} value={rule.mechanism ?? ""} onChange={(e) => patchRule(index, { mechanism: e.target.value })} />
            </Field>
            <Field label="Interpretação clínica">
              <Textarea rows={4} value={rule.interpretation ?? ""} onChange={(e) => patchRule(index, { interpretation: e.target.value })} />
            </Field>
            <Field label="Ação / resposta">
              <Textarea rows={4} value={rule.action ?? ""} onChange={(e) => patchRule(index, { action: e.target.value })} />
            </Field>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Fenótipos agregados">
              <Input value={arr(rule.phenotype)} onChange={(e) => patchArray(index, "phenotype", e.target.value)} placeholder="taquicardia, ansiedade, sedação" />
            </Field>
            <Field label="Monitorização">
              <Input value={arr(rule.monitor)} onChange={(e) => patchArray(index, "monitor", e.target.value)} placeholder="PA, FC, SpO2" />
            </Field>
            <Field label="Red flags">
              <Input value={arr(rule.red_flags)} onChange={(e) => patchArray(index, "red_flags", e.target.value)} placeholder="síncope, FR baixa" />
            </Field>
            <Field label="Modificadores do paciente">
              <Input value={arr(rule.patient_modifiers)} onChange={(e) => patchArray(index, "patient_modifiers", e.target.value)} placeholder="idade, função renal, vulnerabilidade" />
            </Field>
            <Field label="Via">
              <Input value={arr(rule.route_modifiers)} onChange={(e) => patchArray(index, "route_modifiers", e.target.value)} placeholder="IV, oral, inalado" />
            </Field>
            <Field label="Formulação">
              <Input value={arr(rule.formulation_modifiers)} onChange={(e) => patchArray(index, "formulation_modifiers", e.target.value)} placeholder="IR, XR, solução" />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Severidade">
              <Select value={rule.severity} onChange={(v) => patchRule(index, { severity: v as any })} options={["informativa","monitorar","cautela","relevante","urgente"]} />
            </Field>
            <Field label="Confiança">
              <Select value={rule.confidence} onChange={(v) => patchRule(index, { confidence: v as any })} options={["baixa","moderada","alta"]} />
            </Field>
            <Field label="Grau de evidência">
              <Select value={rule.evidence_grade} onChange={(v) => patchRule(index, { evidence_grade: v as any })} options={["A","B","C","D","desconhecida"]} />
            </Field>
            <Field label="Dependência da dose">
              <Select value={rule.dose_dependence} onChange={(v) => patchRule(index, { dose_dependence: v as any })} options={["baixa","moderada","alta","desconhecida"]} />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Janela aguda (h)">
              <Input type="number" value={rule.acute_window_hours ?? 6} onChange={(e) => patchRule(index, { acute_window_hours: Number(e.target.value) || 0 })} />
            </Field>
            <Field label="Janela residual (h)">
              <Input type="number" value={rule.residual_window_hours ?? 48} onChange={(e) => patchRule(index, { residual_window_hours: Number(e.target.value) || 0 })} />
            </Field>
            <Field label="Relevância temporal">
              <Select value={rule.temporal_relevance} onChange={(v) => patchRule(index, { temporal_relevance: v as any })} options={["aguda","residual","manutencao","qualquer"]} />
            </Field>
            <Field label="Manutenção ainda relevante">
              <Select value={rule.maintenance_relevant ? "sim" : "nao"} onChange={(v) => patchRule(index, { maintenance_relevant: v === "sim" })} options={["sim","nao"]} />
            </Field>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Fonte">
              <Input value={rule.source ?? ""} onChange={(e) => patchRule(index, { source: e.target.value })} placeholder="Bula FDA/ANVISA, guideline, revisão..." />
            </Field>
            <Field label="URL da fonte">
              <Input value={rule.source_url ?? ""} onChange={(e) => patchRule(index, { source_url: e.target.value })} placeholder="https://..." />
            </Field>
          </div>
          <Field label="Notas / limitações"><Textarea rows={3} value={rule.notes ?? ""} onChange={(e) => patchRule(index, { notes: e.target.value })} /></Field>
        </div>
      ))}

      <div className="text-[10px] text-muted-foreground">
        Grau A/B/C/D é um campo de rastreabilidade da base, não um cálculo automático de qualidade. “Desconhecida” não é convertida em baixa relevância.
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <div className="min-w-0 space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>;
}

function Select({ value, onChange, options }: { value?: string | null; onChange: (value: string) => void; options: string[] }) {
  return (
    <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
      {options.map((option) => <option key={option} value={option}>{option}</option>)}
    </select>
  );
}
